import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import AdditionalInformationInfo from './summaryIncludes/AdditionalInformationInfo';
import CurrentCoverageInfo from './summaryIncludes/CurrentCoverageInfo';
import EmployerCoverageInfo from './summaryIncludes/EmployerCoverageInfo';
import HraInfo from './summaryIncludes/HraInfo';
import MedicalEmergencyInfo from './summaryIncludes/MedicalEmergencyInfo';
import ReconcileAptcInfo from './summaryIncludes/ReconcileAptcInfo';
import SubHeader from '../../../headers/SubHeader';
import SummaryFilter from '@components/ssap/pages/pageIncludes/summary/SummaryFilter';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';
import {
    useExchangeAndProgramNames,
    selectIfFinalReviewPageActive,
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIfLivingArrangement,
    selectIfShowStateEmployeeHealthBenefitPlanEnabled,
    selectIfSsapInReviewMode,
    selectIsItuQuestionsDisabled,
} from '@reducers/appReducer';

// Constants

import { PAGES } from '@constants/ssap/flows/pagesTypes';
import PageHeader from '@components/ssap/headers/PageHeader';

// Types

import { SsapSummaryType } from '@components/ssap/SsapTypes';
import EmployeeStateHealthBenefitInfo from './summaryIncludes/EmployeeStateHealthBenefit';


const AdditionalInformationSummary = props => {

    const {
        adjustedFlow,
        allStatuses,
        alwaysDisplayMedicaidQuestions,
        buttonContainer: ButtonContainer,
        householdMembers,
        indexMapper,
        inlineCurrentIncomeEnabled,
        isRenewalFlow,
        isState,
        stateCode,
        itfFeatureEnabled,
        previousData,
        serverDate,
        taxHousehold
    } = props;

    const intlApi = useIntl();
    const collectLivingArrangementEnabled = useSelector(selectIfLivingArrangement);
    const isItuQuestionsDisabled = useSelector(selectIsItuQuestionsDisabled);
    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const showStateEmployeeHealthBenefitPlan = useSelector(selectIfShowStateEmployeeHealthBenefitPlanEnabled);
    const isFinalSummaryPage = useSelector(selectIfFinalReviewPageActive);
    const isSsapInReviewMode = useSelector(selectIfSsapInReviewMode);
    const exchangeAndProgramNames = useExchangeAndProgramNames();

    const checkHouseholdMember = idx => {
        
        if (adjustedFlow === undefined) {
            return true;
        } else {
            const pagesListToCheck = [
                PAGES.OTHER_HEALTH_COVERAGE_PAGE,
                PAGES.RECONCILIATION_IN_APTC_PAGE,
                PAGES.EMPLOYER_COVERAGE_DETAIL_PAGE,
                PAGES.STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE,
                PAGES.ADDITIONAL_INFORMATION_PAGE
            ];

            return !pagesListToCheck.every(pageName => !(p => p ? p.isActive : false)(adjustedFlow.find(pg => pg.pageName === pageName && pg.hhm === idx)));
        }
    };
    
    const getHouseholdMembersSummaries = () => householdMembers
        .map((member, idx) => {

            const {
                applicantGuid,
                applyingForCoverageIndicator,
                livingArrangement,
                mcoSelection,
                name,
                householdContact: {
                    homeAddress: {
                        state
                    }
                },
                healthCoverage,
                healthCoverage: {
                    offeredIchraIndicator,
                    currentOtherInsurance
                },
                healthReimbursementArrangement,
                employerSponsoredCoverage,
                taxHouseholdComposition: {
                    independentTaxFiler
                },
                americanIndianAlaskaNative: {
                    memberOfFederallyRecognizedTribeIndicator
                },
                inlineEligibility: {
                    preliminaryMedicaidStatus,
                    preliminaryEmergencyMedicaidStatus,
                    preliminaryChipStatus,
                    preliminaryAptcCsrStatus
                },
                citizenshipImmigrationStatus: {
                    citizenshipStatusIndicator,
                    naturalizedCitizenshipIndicator
                },
                medicalEmergency,
            } = member;

            const params = {
                alwaysDisplayMedicaidQuestions,
                applyingForCoverageIndicator,
                inlineCurrentIncomeEnabled,
                preliminaryChipStatus,
                preliminaryEmergencyMedicaidStatus,
                preliminaryMedicaidStatus,
            };

            const indianHealthServicesParams = {
                ...params,
                isItuQuestionsDisabled,
                memberOfFederallyRecognizedTribeIndicator,
            };

            const employerCoverageParams = {
                currentOtherInsurance,
                checkHRA: false,
                hhm: member,
                hhmStatus: allStatuses[idx]?.hhmStatus,
                inlineCurrentIncomeEnabled,
                inlineEligibilityIgnoredForEscPage,
                preliminaryAptcCsrStatus,
                serverDate
            };

            const employeeHealthBenefitParams = {
                ...params,
                showStateEmployeeHealthBenefitPlan,
                stateCode,
            };

            const medicalEmergencyParams = {
                ...params,
                citizenshipStatusIndicator,
                naturalizedCitizenshipIndicator,
                stateCode,
            };

            const isIndianHealthServicesShown = questionsVisibility.isIndianHealthServicesShown(indianHealthServicesParams);
            const isEmployeeHealthBenefitShown = questionsVisibility.isEmployeeStateHealthBenefitShown(employeeHealthBenefitParams);
            const isEmployerCoverageShown = questionsVisibility.isESCorHRAShown(employerCoverageParams);
            const isHealthReimbursementShown = questionsVisibility.isESCorHRAShown({ ...employerCoverageParams, checkHRA: true });
            const isLivingArrangementShown = questionsVisibility.isLivingArrangementShown({ collectLivingArrangementEnabled, preliminaryMedicaidStatus });
            const isMedicalEmergencyShown = questionsVisibility.isMedicalEmergencyShown(medicalEmergencyParams);

            if(!checkHouseholdMember(idx)) {
                return null;
            }

            return (
                <SummaryFilter
                    key = { applicantGuid }
                    indexMapper = { indexMapper }
                    summaryId = { SsapSummaryType.ADDITIONAL_INFORMATION.processReplacements({ idx }) }
                >
                    <div className = 'summary__block margin-b-20' >
                        <div className = 'summary__header' >
                            <SubHeader
                                dataTestId={`Additional-Info-Header-${applicantGuid}`}
                                text = 'ssap.subheader.non_internationalized_text'
                                textReplacements = { {
                                    text: (
                                        <FormattedMessage
                                            id = 'ssap.subheader.person_name_primary_contact'
                                            values = { {
                                                personName: normalizeNameOfPerson(name),
                                                primaryContact: (
                                                    <FormattedMessage
                                                        id = { idx === 0 ? 'common.primary_contact' : 'common.no_text' }
                                                    />
                                                )
                                            } }
                                        />
                                    )
                                } }
                                headerClassName = 'gi-heading-alt'
                                additionalElement = {
                                    (
                                        <ButtonContainer
                                            args={ [ idx, PAGES.OTHER_HEALTH_COVERAGE_PAGE ] }
                                            type = 'editButton_secondary'
                                            ariaLabel={
                                                intlApi.formatMessage(
                                                    { id: 'ssap.edit.additional_information' },
                                                    { firstName: name.firstName, lastName: name.lastName },
                                                )
                                            }
                                        />
                                    )
                                }
                                additionalElementClassName = 'header-btn'
                            />
                        </div>
                        <div className = 'summary__body' >
                            <div className = 'grid-row' >
                                <CurrentCoverageInfo
                                    { ...{
                                        applyingForCoverageIndicator,
                                        exchangeAndProgramNames,
                                        healthCoverage,
                                        idx,
                                        isState,
                                    } }
                                />
                            </div>

                            <div className = 'grid-row' >
                                <ReconcileAptcInfo
                                    { ...{
                                        independentTaxFiler,
                                        isRenewalFlow,
                                        isState,
                                        itfFeatureEnabled,
                                        member,
                                        name,
                                        serverDate,
                                        taxHousehold
                                    } }
                                />
                            </div>

                            <div className = 'grid-row' >
                                <EmployerCoverageInfo
                                    { ...{
                                        applicantGuid,
                                        employerSponsoredCoverage,
                                        healthCoverage,
                                        householdMembers,
                                        idx,
                                        isEmployerCoverageShown,
                                        previousData,
                                        state,
                                        isState,
                                        isFinalSummaryPage,
                                        isSsapInReviewMode
                                    } }
                                />
                                <EmployeeStateHealthBenefitInfo
                                    {...{
                                        healthCoverage,
                                        idx,
                                        name,
                                        state,
                                        isEmployeeHealthBenefitShown
                                    }}
                                />
                            </div>
                            <div className = 'grid-row' >
                                <HraInfo
                                    { ...{
                                        applicantGuid,
                                        healthReimbursementArrangement,
                                        householdMembers,
                                        idx,
                                        isFinalSummaryPage,
                                        isHealthReimbursementShown,
                                        isSsapInReviewMode,
                                        offeredIchraIndicator,
                                        previousData
                                    } }
                                />
                            </div>
                            <div className = 'grid-row' >
                                <AdditionalInformationInfo
                                    { ...{
                                        alwaysDisplayMedicaidQuestions,
                                        exchangeAndProgramNames,
                                        idx,
                                        inlineCurrentIncomeEnabled,
                                        isState,
                                        healthCoverage,
                                        livingArrangement,
                                        isLivingArrangementShown,
                                        mcoSelection,
                                        name,
                                        isIndianHealthServicesShown,
                                        preliminaryMedicaidStatus
                                    } }
                                />
                                <MedicalEmergencyInfo
                                    {...{
                                        idx,
                                        name,
                                        medicalEmergency,
                                        isMedicalEmergencyShown
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </SummaryFilter>
            );
        });

    return (
        <SummaryFilter summaryId = { SsapSummaryType.ADDITIONAL_INFORMATION.processReplacements({ idx: 'ROOT' }) }>
            <div className = 'final-review-heading break_before' >
                <PageHeader
                    text = 'ssap.subheader.additional_info'
                    headerClassName = 'gi-heading-alt'
                />
            </div>
            { getHouseholdMembersSummaries() }
        </SummaryFilter>
    );
};

export default AdditionalInformationSummary;
